import React from 'react';
import Icon from '../ui/Icon';
import Avatar from '../ui/Avatar';
import { useAuth } from '../../context/AuthContext';
import { useApp }  from '../../context/AppContext';
import { MENU_ITEMS, SERVICES } from '../../data/mockData';
import { useWallet } from '../../hooks/useWallet';

const QUICK_REQUESTS = [
  { serviceId: 1, label: 'Request plumber',     icon: '🔧' },
  { serviceId: 2, label: 'Request electrician', icon: '⚡' },
  { serviceId: 3, label: 'Request cleaning',    icon: '🧹' },
];

const SideMenu = () => {
  const { user, logout } = useAuth();
  const { menuOpen, closeMenu, activeTab, setActiveTab, setPreselectedService } = useApp();
  const { data: walletData, isLoading: walletLoading } = useWallet();

  const handleNav = (id) => {
    setActiveTab(id === 'new' ? 'new' : id);
    closeMenu();
  };

  const handleQuickRequest = (serviceId) => {
    const svc = SERVICES.find((s) => s.id === serviceId);
    if (svc) setPreselectedService(svc);
    setActiveTab('new');
    closeMenu();
  };

  return (
    <>
      {menuOpen && (
        <div
          className="absolute inset-0 z-40 backdrop animate-fadeIn"
          onClick={closeMenu}
        />
      )}

      <aside
        className={`
          absolute top-0 left-0 h-full w-[78%] max-w-[300px] z-50
          bg-surface-800 flex flex-col
          border-r border-surface-500
          transition-transform duration-300 ease-out
          ${menuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ boxShadow: menuOpen ? '8px 0 32px rgba(0,0,0,0.10)' : 'none' }}
      >
        <div className="noise-overlay rounded-none" />

        {/* Header */}
        <div className="relative px-5 pt-12 pb-5 border-b border-surface-500">
          <button
            onClick={closeMenu}
            className="absolute top-4 right-4 w-8 h-8 rounded-xl bg-surface-700 flex items-center justify-center text-surface-200 hover:text-slate-800 hover:bg-surface-500 transition-colors"
          >
            <Icon name="x" size={15} />
          </button>

          <div className="flex items-center gap-3">
            <Avatar initials={user?.avatar || [user?.first_name?.[0], user?.last_name?.[0]].filter(Boolean).join('') || user?.name?.[0] || 'U'} size="xl" ring />
            <div className="min-w-0">
              <h3 className="font-syne font-bold text-slate-800 text-base leading-tight truncate">
                {user?.name || user?.full_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'User'}
              </h3>
              <p className="font-dm text-surface-300 text-xs uppercase tracking-wider mt-0.5">Wallet Balance</p>
              {walletLoading ? (
                <div className="h-4 w-20 rounded-md shimmer-bg mt-1" />
              ) : (
                <p className="font-syne font-bold text-slate-800 text-sm leading-tight">
                  {walletData?.wallet?.currency ?? 'GHS'}{' '}
                  {walletData?.wallet?.balance != null
                    ? Number(walletData.wallet.balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : '0.00'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {MENU_ITEMS.map((item, i) => {
            const active = activeTab === item.id;
            return (
              <React.Fragment key={item.id}>
                <button
                  onClick={() => handleNav(item.id)}
                  className={`
                    w-full flex items-center gap-3.5 px-4 py-3.5 rounded-2xl mb-1
                    font-dm font-medium text-sm transition-all duration-150 text-left press-scale
                    ${active
                      ? 'bg-brand-50 text-brand-600 border border-brand-200'
                      : 'text-slate-600 hover:bg-surface-700 hover:text-slate-800 border border-transparent'}
                  `}
                  style={{ animationDelay: `${i * 0.04}s` }}
                >
                  <span className={`${active ? 'text-brand-600' : 'text-surface-300'}`}>
                    <Icon name={item.icon} size={18} />
                  </span>
                  {item.label}
                  {active && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-brand-600" />
                  )}
                </button>

                {/* Quick request shortcuts — inserted after My Properties */}
                {item.id === 'properties' && (
                  <div className="mb-1">
                    {QUICK_REQUESTS.map((qr) => (
                      <button
                        key={qr.serviceId}
                        onClick={() => handleQuickRequest(qr.serviceId)}
                        className="w-full flex items-center gap-3.5 px-4 py-3 rounded-2xl mb-1 font-dm font-medium text-sm text-slate-600 hover:bg-surface-700 hover:text-slate-800 border border-transparent transition-all duration-150 press-scale text-left"
                      >
                        <span className="text-base w-[18px] text-center">{qr.icon}</span>
                        {qr.label}
                      </button>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Footer – Logout */}
        <div className="px-3 pb-8 border-t border-surface-500 pt-4">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3.5 px-4 py-3.5 rounded-2xl font-dm font-medium text-sm text-rose-500 hover:bg-rose-50 transition-all duration-150 press-scale"
          >
            <Icon name="logout" size={18} />
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

export default SideMenu;
